/**
 * QUOTATION FORM — creazione e modifica del preventivo.
 *
 * ## Tre ingressi contestuali
 *
 * Oltre alla creazione da lista, il form si apre da un contatto
 * (`?personId=X`), da un'azienda (`?businessId=X`) e da un messaggio
 * (`?messageId=X`). Cambiano tre cose: cosa viene pre-compilato, quali
 * relazioni si creano dopo il salvataggio, e dove si torna.
 *
 * Tutte e tre usano solo entità e macchinari core — Person, Business, Message,
 * Relationship — quindi vivono qui e non in un plugin.
 *
 * ## Cosa NON è bloccante
 *
 * Solo la creazione del preventivo lo è. Le relazioni che si creano dopo sono
 * best-effort: se falliscono l'utente vede un avviso ma il preventivo resta,
 * perché è già stato salvato e nasconderlo sarebbe peggio che segnalare un
 * collegamento mancante.
 *
 * ## I sotto-form dei plugin sono una via di mezzo
 *
 * Non sono best-effort come le relazioni, e non sono bloccanti come la
 * creazione: sono *dati del preventivo* che stanno in un'altra tabella e
 * viaggiano su un'altra chiamata REST. Se falliscono, il preventivo esiste ma è
 * incompleto — quindi non si può né ignorarlo né fingere che non sia stato
 * creato, perché lo è.
 *
 * La risposta è uno stato dichiarato: si resta sulla pagina, si dice cosa è
 * successo e si offre di ritentare i soli plugin falliti. Il rollback non è
 * un'opzione: sono transazioni distinte, e simularlo lato client vorrebbe dire
 * cancellare un preventivo che l'utente ha appena visto nascere.
 *
 * Il meccanismo di registrazione è in `quotationFormContext.ts`.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import { useAsyncAutocomplete } from '@crm/shared';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import type { LookupOption } from '../../components/EditableField';
import { useCurrentGroupId } from '../../auth/authSelectors';
import { ChevronDownGlyph } from '../../components/icons';
import { useToast } from '../../components/useToast';
import { useSlotContributors } from '../../plugin/useSlotContributors';
import { getEntityFqn } from '../../entityRegistry/entityRegistry';
import { createBidirectionalRelationship, fetchRelationships } from '../relationship/relationshipThunks';
import { relationshipApi } from '../relationship/relationshipApi';
import { personApi } from '../person/personApi';
import { messageApi } from '../message/messageApi';
import type { Message } from '../message/messageTypes';
import { businessApi } from '../business/businessApi';
import { fetchPipelines } from '../pipeline/pipelineThunks';
import { pipelinesOfType } from '../pipeline/pipelineEntityTypes';
import type { PipelineSliceState } from '../pipeline/pipelineTypes';
import { findOwningPipeline, pipelineStateLabel, sortedStates } from '../pipeline/pipelineUtils';
import { fetchQuotation, createQuotation, updateQuotation } from './quotationThunks';
import { QuotationFormContext, type QuotationFormExtension } from './quotationFormContext';
import { clearCurrentQuotation } from './quotationSlice';
import { linkInfosToQuotation } from './messageRelationships';
import {
  QUOTATION_STATUSES,
  type QuotationState,
  type QuotationCreatePayload,
  type QuotationStatus,
} from './quotationTypes';

/**
 * Stato del form. `amount` è una stringa mentre l'utente digita: un campo
 * numerico vuoto non è `0`, ed è la distinzione che permette di non inviare
 * zero quando l'importo semplicemente non è stato compilato.
 */
type FormState = Omit<QuotationCreatePayload, 'amount'> & { amount: string };

export default function QuotationForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const toast = useToast();
  const isEdit = Boolean(id);
  const { currentItem } = useAppSelector((state) => state.quotations as QuotationState);
  const { items: allPipelines } = useAppSelector((state) => state.pipelines as PipelineSliceState);
  // TS-117: solo i funnel DEL PREVENTIVO. Lo slice `pipelines` e' condiviso e contiene anche quelli
  // di altre entita': senza questo filtro comparirebbero nella cascata, e sceglierne uno metterebbe
  // il preventivo in uno stato che non gli appartiene.
  const pipelines = useMemo(
    () => pipelinesOfType(allPipelines, getEntityFqn('Quotation')),
    [allPipelines],
  );
  const extraContributors = useSlotContributors('Quotation', 'form:extra');

  const [searchParams] = useSearchParams();
  const sourceMessageId = searchParams.get('messageId');
  const sourcePersonId = searchParams.get('personId');
  const sourceBusinessId = searchParams.get('businessId');
  const groupId = useCurrentGroupId();
  const [linkedMessage, setLinkedMessage] = useState<Message | null>(null);
  // Valorizzato quando il preventivo è stato salvato ma i campi di uno o più
  // plugin no. Non è un errore da toast: è uno stato in cui la pagina resta
  // finché l'utente non ritenta o non se ne va consapevolmente.
  const [partialSave, setPartialSave] = useState<{ quotationId: number; failedKeys: string[] } | null>(null);

  const [form, setForm] = useState<FormState>({
    externalCode: '', businessId: 0, title: '',
    dueDate: '', amount: '', notes: '', status: 'draft', currentStateId: 0,
  });

  // Pipeline selezionata nella cascata (UI-only): pilota le opzioni della combo
  // stato. Non fa parte del payload — al backend va solo `currentStateId`.
  const [pipelineId, setPipelineId] = useState(0);

  // Lo stato del preventivo è obbligatorio SOLO se esiste almeno una pipeline
  // (TS-101): senza pipeline la cascata è nascosta e la creazione procede senza.
  const hasPipelines = pipelines.length > 0;
  const pipelineStates = useMemo(
    () => sortedStates(pipelines.find((x) => x.pipelineId === pipelineId)),
    [pipelines, pipelineId],
  );

  // Cliente: l'anagrafica non si carica intera, si cerca sul server mentre si
  // digita. L'etichetta del valore scelto si tiene a parte, perché chi arriva
  // pre-compilato (da azienda, contatto, messaggio, modifica) porta solo l'id.
  const [client, setClient] = useState<LookupOption | null>(null);

  const fetchClientOptions = useCallback(
    async (query: string): Promise<LookupOption[]> => {
      if (!groupId) return [];
      const page = await businessApi.getAll(groupId, { search: query || undefined, pageSize: 20 });
      return page.items.map((b) => ({ id: b.businessId, label: b.name }));
    },
    [groupId],
  );

  const {
    loading: clientLoading,
    setQuery: setClientQuery,
    autocompleteProps: clientAutocompleteProps,
  } = useAsyncAutocomplete<LookupOption>({ fetcher: fetchClientOptions });

  // Il nome del cliente pre-compilato va anche nella casella: l'input è
  // controllato dalla query del hook, e senza resterebbe vuoto.
  useEffect(() => {
    const bid = form.businessId;
    if (bid <= 0 || client?.id === bid) return;
    let cancelled = false;
    businessApi.getById(bid)
      .then((b) => b.name)
      .catch(() => `#${bid}`)
      .then((label) => {
        if (cancelled) return;
        setClient({ id: bid, label });
        setClientQuery(label);
      });
    return () => { cancelled = true; };
  }, [form.businessId, client?.id, setClientQuery]);

  // Aperto dalla scheda di un'azienda, il cliente è quell'azienda: si vede ma
  // non si cambia, perché al salvataggio il preventivo si collega a lei.
  const clientLocked = !isEdit && Boolean(sourceBusinessId);

  useEffect(() => {
    if (isEdit && id) dispatch(fetchQuotation(Number(id)));
    return () => { dispatch(clearCurrentQuotation()); };
  }, [isEdit, id, dispatch]);

  // Pipeline per la cascata pipeline->stato (states embedded nella lista).
  useEffect(() => {
    // Sul totale NON filtrato: se il sito ha solo funnel di altre entita', l'elenco filtrato resta
    // vuoto e si rifarebbe la fetch a ogni render.
    if (allPipelines.length === 0) dispatch(fetchPipelines({ pageSize: 1000 }));
  }, [allPipelines.length, dispatch]);

  useEffect(() => {
    if (isEdit && currentItem) {
      setForm({
        externalCode: currentItem.externalCode ?? '',
        businessId: currentItem.businessId,
        title: currentItem.title,
        dueDate: currentItem.dueDate ? currentItem.dueDate.split('T')[0] : '',
        amount: currentItem.amount ? String(currentItem.amount) : '',
        notes: currentItem.notes ?? '',
        status: currentItem.status,
        currentStateId: currentItem.currentStateId ?? 0,
      });
    }
  }, [isEdit, currentItem]);

  // In modifica la combo pipeline va pre-selezionata a partire dallo stato del
  // preventivo: si cerca quale pipeline contiene `currentStateId`. Dipende dal
  // caricamento delle pipeline (states embedded), quindi vive in un effetto a sé.
  useEffect(() => {
    if (!isEdit || !currentItem || pipelines.length === 0) return;
    const owner = findOwningPipeline(pipelines, currentItem.currentStateId ?? 0);
    if (owner) setPipelineId(owner.pipelineId);
  }, [isEdit, currentItem, pipelines]);

  // Creazione da contatto: pre-compila l'azienda principale del contatto, cioè
  // la Relationship marcata `primary`. Best-effort — se non c'è, l'utente
  // sceglie a mano.
  useEffect(() => {
    if (isEdit || !sourcePersonId || !groupId) return;
    const pid = Number(sourcePersonId);
    if (!Number.isFinite(pid)) return;
    const personFqn = getEntityFqn('Person');
    const businessFqn = getEntityFqn('Business');
    if (!personFqn || !businessFqn) return;
    let cancelled = false;
    (async () => {
      try {
        const rels = await relationshipApi.getByEntityBidirectional(groupId, personFqn, pid);
        const primary = rels.find((r) => r.className2 === businessFqn && r.primary === true);
        if (!cancelled && primary) {
          setForm((prev) => ({ ...prev, businessId: primary.classPK2 || prev.businessId }));
        }
      } catch {
        // best-effort: businessId resta 0
      }
    })();
    return () => { cancelled = true; };
  }, [isEdit, sourcePersonId, groupId]);

  // Creazione da azienda: pre-compila l'azienda e basta.
  useEffect(() => {
    if (isEdit || !sourceBusinessId) return;
    const bid = Number(sourceBusinessId);
    if (!Number.isFinite(bid)) return;
    setForm((prev) => ({ ...prev, businessId: bid }));
  }, [isEdit, sourceBusinessId]);

  // Creazione da messaggio: titolo dall'oggetto, azienda dal dato estratto
  // "client" se già a sistema. Il corpo NON finisce nelle note: sarebbe solo
  // rumore, i dati utili del messaggio stanno negli estratti.
  useEffect(() => {
    if (isEdit || !sourceMessageId) return;
    const mid = Number(sourceMessageId);
    if (!Number.isFinite(mid)) return;
    let cancelled = false;
    (async () => {
      try {
        const msg: Message = await messageApi.getById(mid);
        if (cancelled) return;
        setLinkedMessage(msg);
        const infos = (msg.extractorInfos ?? []).flatMap((g) => g.infos);
        const clientInfo = infos.find((i) => i.label === 'client' && i.classPK > 0);
        setForm((prev) => ({
          ...prev,
          title: msg.subject || prev.title,
          businessId: clientInfo?.classPK ?? prev.businessId,
        }));
      } catch {
        // Pre-compilazione best-effort: se il fetch fallisce l'utente compila a
        // mano, e il collegamento al messaggio si crea comunque al salvataggio.
      }
    })();
    return () => { cancelled = true; };
  }, [isEdit, sourceMessageId]);

  // Uscita: una sola destinazione per il link in alto, il pulsante in basso e
  // il salvataggio, perché sono lo stesso gesto — si torna da dove si è entrati.
  // In modifica quel punto è la scheda del preventivo: ci si arriva dal suo
  // pulsante "Modifica", e anche venendo dalla lista atterrare sul record appena
  // modificato è più utile che tornare all'elenco.
  const exitTo = sourceMessageId
    ? `/inbox?messageId=${sourceMessageId}`
    : isEdit && id
      ? `/quotations/${id}`
      : '/quotations';

  // Registrazioni dei sotto-form dei plugin. In un ref e non in uno state
  // perché cambiano a ogni battuta sulla tastiera dentro il sotto-form, e
  // rirenderizzare il form del core a ogni carattere digitato in un altro
  // componente non serve a niente.
  const extensionsRef = useRef(new Map<string, QuotationFormExtension>());

  const registerExtension = useCallback((extension: QuotationFormExtension) => {
    extensionsRef.current.set(extension.key, extension);
    return () => { extensionsRef.current.delete(extension.key); };
  }, []);

  const formContext = useMemo(() => ({ registerExtension }), [registerExtension]);

  /**
   * Esegue i salvataggi dei plugin e restituisce le chiavi di quelli falliti.
   *
   * In sequenza e non in parallelo: sono scritture, e un ordine deterministico
   * rende riproducibile un eventuale problema. I plugin sono pochi e le
   * chiamate brevi, quindi non c'è niente da guadagnare a sovrapporle.
   *
   * @param onlyKeys se valorizzato, riprova solo queste chiavi
   */
  async function runExtensions(quotationId: number, onlyKeys?: string[]): Promise<string[]> {
    const failed: string[] = [];
    for (const extension of extensionsRef.current.values()) {
      if (onlyKeys && !onlyKeys.includes(extension.key)) continue;
      try {
        await extension.submit(quotationId);
      } catch {
        failed.push(extension.key);
      }
    }
    return failed;
  }

  /**
   * Ritenta i soli plugin falliti. Il preventivo non si ricrea: esiste già, ed
   * è proprio il punto — la parte del core è andata a buon fine.
   */
  async function retryExtensions() {
    if (!partialSave) return;
    const failedKeys = await runExtensions(partialSave.quotationId, partialSave.failedKeys);
    if (failedKeys.length > 0) {
      setPartialSave({ ...partialSave, failedKeys });
      return;
    }
    setPartialSave(null);
    toast({ severity: 'success', message: t('common.saved') });
    if (isEdit) navigate(exitTo);
    else await finishCreation(partialSave.quotationId);
  }

  // Cambio pipeline nella cascata: resetta lo stato scelto (il parent governa il
  // reset del figlio, come CountrySelect/RegionSelect) e pre-seleziona lo stato
  // iniziale della nuova pipeline, che è il default naturale per un preventivo.
  function handlePipelineChange(newPipelineId: number) {
    setPipelineId(newPipelineId);
    const p = pipelines.find((x) => x.pipelineId === newPipelineId);
    const initial = p?.states.find((s) => s.kind === 'initial');
    setForm((prev) => ({ ...prev, currentStateId: initial?.pipelineStateId ?? 0 }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();

    // I guard JS non sono ridondanti rispetto al `required` di MUI: il select
    // con sentinel 0 non fa scattare il required HTML5, e una stringa di soli
    // spazi lo supera pur non passando la validazione server-side.
    if (form.businessId <= 0) {
      toast({ severity: 'error', message: t('quotation.form.clientRequired') });
      return;
    }
    if (!form.title.trim()) {
      toast({ severity: 'error', message: t('quotation.form.titleRequired') });
      return;
    }
    // Stato obbligatorio solo se esiste ≥1 pipeline (TS-101). Il sentinel 0 non
    // fa scattare il required HTML5 della combo, quindi il guard JS è necessario.
    if (hasPipelines && (form.currentStateId ?? 0) <= 0) {
      toast({ severity: 'error', message: t('quotation.form.stateRequired') });
      return;
    }

    const payload: QuotationCreatePayload = {
      externalCode: form.externalCode?.trim() || null,
      businessId: form.businessId,
      title: form.title.trim(),
      dueDate: form.dueDate ? `${form.dueDate}T00:00:00Z` : null,
      amount: form.amount === '' ? 0 : Number(form.amount),
      notes: form.notes ?? null,
      currentStateId: form.currentStateId ?? 0,
    };

    // Branch MODIFICA: l'update è l'operazione primaria, l'errore è bloccante.
    if (isEdit && id) {
      try {
        await dispatch(updateQuotation({
          ...payload,
          status: form.status,
          quotationId: Number(id),
        })).unwrap();
      } catch (err) {
        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        return;
      }
      const failedKeys = await runExtensions(Number(id));
      if (failedKeys.length > 0) {
        setPartialSave({ quotationId: Number(id), failedKeys });
        return;
      }
      toast({ severity: 'success', message: t('common.saved') });
      navigate(exitTo);
      return;
    }

    // Branch CREAZIONE. Lo status non viene inviato: il backend lo ignorerebbe,
    // il preventivo nasce sempre in bozza.
    let newQuotationId: number;
    try {
      const created = await dispatch(createQuotation(payload)).unwrap();
      newQuotationId = created.quotationId;
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
      return;
    }

    // I sotto-form dei plugin salvano DOPO la creazione, perché solo adesso
    // esiste l'id a cui agganciare i loro campi. Se qualcuno fallisce ci si
    // ferma qui: il preventivo esiste, i suoi campi di estensione no, e
    // andarsene lasciandolo a metà senza dirlo sarebbe il modo peggiore di
    // gestirlo. Il messaggio di successo arriva solo quando ha salvato tutto.
    const failedKeys = await runExtensions(newQuotationId);
    if (failedKeys.length > 0) {
      setPartialSave({ quotationId: newQuotationId, failedKeys });
      return;
    }
    toast({ severity: 'success', message: t('common.saved') });

    await finishCreation(newQuotationId);
  }

  /**
   * Collegamenti contestuali e uscita. Estratta da `handleSubmit` perché ha due
   * chiamanti: il salvataggio normale e il ritentativo dopo un salvataggio
   * parziale, che deve poter riprendere da dove si era fermato invece di
   * ricreare un preventivo che esiste già.
   */
  async function finishCreation(newQuotationId: number) {

    const quotationFqn = getEntityFqn('Quotation');

    // Da contatto: relazione Person <-> Quotation, poi si torna al contatto.
    if (sourcePersonId) {
      const pid = Number(sourcePersonId);
      const personFqn = getEntityFqn('Person');
      if (quotationFqn && personFqn && Number.isFinite(pid)) {
        try {
          await dispatch(createBidirectionalRelationship({
            className1: personFqn, classPK1: pid,
            className2: quotationFqn, classPK2: newQuotationId,
            descriptionForward: null, descriptionReverse: null,
          })).unwrap();
        } catch {
          toast({ severity: 'warning', message: t('quotation.linkSourceFailed') });
        }
      }
      navigate(`/contacts/${pid}`);
      return;
    }

    // Da azienda: relazione Business <-> Quotation, poi si torna all'azienda.
    if (sourceBusinessId) {
      const bid = Number(sourceBusinessId);
      const businessFqn = getEntityFqn('Business');
      if (quotationFqn && businessFqn && Number.isFinite(bid)) {
        try {
          await dispatch(createBidirectionalRelationship({
            className1: businessFqn, classPK1: bid,
            className2: quotationFqn, classPK2: newQuotationId,
            descriptionForward: null, descriptionReverse: null,
          })).unwrap();
        } catch {
          toast({ severity: 'warning', message: t('quotation.linkSourceFailed') });
        }
      }
      navigate(`/companies/${bid}`);
      return;
    }

    // Da messaggio: la relazione col messaggio, più quelle inferite dai dati
    // estratti. Tutte best-effort: il preventivo esiste già.
    if (sourceMessageId) {
      const mid = Number(sourceMessageId);
      const messageFqn = getEntityFqn('Message');
      const businessFqn = getEntityFqn('Business');
      if (quotationFqn && Number.isFinite(mid)) {
        if (messageFqn) {
          try {
            await dispatch(createBidirectionalRelationship({
              className1: messageFqn, classPK1: mid,
              className2: quotationFqn, classPK2: newQuotationId,
              descriptionForward: t('quotation.relationship.fromMessage'),
              descriptionReverse: t('quotation.relationship.toMessage'),
            })).unwrap();
          } catch (err) {
            toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
          }
        }
        // Collegamento silente delle entità GIÀ a sistema estratte dal
        // messaggio. Quelle non ancora esistenti (classPK=0) non si toccano:
        // il preventivo non crea anagrafiche per conto suo.
        if (linkedMessage) {
          try {
            await linkInfosToQuotation(
              dispatch,
              (linkedMessage.extractorInfos ?? []).flatMap((g) => g.infos),
              newQuotationId,
              quotationFqn,
              t('quotation.relationship.viaMessage'),
            );
          } catch (err) {
            toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
          }
        }
        // L'azienda cliente va collegata sull'id EFFETTIVO: se è stata scelta a
        // mano non compare fra i dati estratti, quindi il blocco sopra non
        // l'avrebbe presa. Si salta se è già fra gli estratti, per non creare
        // un duplicato.
        const businessAlreadyInInfos = (linkedMessage?.extractorInfos ?? [])
          .flatMap((g) => g.infos)
          .some((i) => i.className === businessFqn && i.classPK === form.businessId);
        if (businessFqn && form.businessId > 0 && !businessAlreadyInInfos) {
          try {
            await dispatch(createBidirectionalRelationship({
              className1: businessFqn, classPK1: form.businessId,
              className2: quotationFqn, classPK2: newQuotationId,
              descriptionForward: t('quotation.relationship.viaMessage'),
              descriptionReverse: t('quotation.relationship.viaMessage'),
            })).unwrap();
          } catch (err) {
            toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
          }
        }
        // Il mittente del messaggio, se corrisponde a una Person ESISTENTE per
        // email esatta. Se non esiste non si crea nulla — a differenza
        // dell'azienda, che l'utente può creare a mano. Blocco indipendente e
        // messo per ultimo: un fallimento qui non tocca i collegamenti già
        // creati sopra.
        const personFqn = getEntityFqn('Person');
        const senderEmail = linkedMessage?.senderEmailAddress?.trim();
        if (personFqn && groupId && senderEmail) {
          try {
            const person = await personApi.getByEmailAddress(groupId, senderEmail);
            const personAlreadyInInfos = person != null && (linkedMessage?.extractorInfos ?? [])
              .flatMap((g) => g.infos)
              .some((i) => i.className === personFqn && i.classPK === person.personId);
            if (person && !personAlreadyInInfos) {
              await dispatch(createBidirectionalRelationship({
                className1: personFqn, classPK1: person.personId,
                className2: quotationFqn, classPK2: newQuotationId,
                descriptionForward: t('quotation.relationship.viaMessage'),
                descriptionReverse: t('quotation.relationship.viaMessage'),
              })).unwrap();
            }
          } catch (err) {
            toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
          }
        }
        // Le relazioni appena create vanno rilette: chi torna sul messaggio
        // deve vedere il preventivo collegato senza ricaricare la pagina.
        if (messageFqn) {
          dispatch(fetchRelationships({ className: messageFqn, classPK: mid }));
        }
      }
      navigate(`/inbox?messageId=${mid}`);
      return;
    }

    navigate('/quotations');
  }

  return (
    // Il link "torna" sta sopra l'intestazione, come nel builder automazioni:
    // e' la via d'uscita, non un elemento del titolo.
    <>
      <Link to={exitTo} className="tpl-back">
        <ChevronDownGlyph />
        {t('quotation.form.back')}
      </Link>
      <div className="page-head">
        <div>
          <h1 className="page-title">{isEdit ? t('quotation.editQuotation') : t('quotation.createQuotation')}</h1>
        </div>
      </div>

      <form className="qform" onSubmit={handleSubmit}>
        <section className="blk">
          <div className="blk__head">
            <h2 className="blk__title">{t('quotation.form.basicInfo')}</h2>
          </div>
          <div className="blk__body">
            {/* Stato del preventivo via cascata pipeline -> stato. Mostrata solo se
                esiste almeno una pipeline: in quel caso lo stato è obbligatorio
                (TS-101). La combo stato dipende dalla pipeline scelta (options dai
                suoi states); il reset/default lo gestisce handlePipelineChange.
                TS-102 sostituirà la scelta manuale con un criterio di filtro. */}
            {hasPipelines && (
              <div className="qform__row">
                <TextField
                  select required
                  label={t('quotation.form.pipeline')}
                  value={pipelineId || ''}
                  onChange={(e) => handlePipelineChange(e.target.value === '' ? 0 : Number(e.target.value))}
                  fullWidth className="form-field"
                >
                  <MenuItem value="">{t('quotation.form.selectPipeline')}</MenuItem>
                  {pipelines.map((p) => <MenuItem key={p.pipelineId} value={p.pipelineId}>{p.name}</MenuItem>)}
                </TextField>
                <TextField
                  select required
                  label={t('quotation.form.state')}
                  value={form.currentStateId || ''}
                  onChange={(e) => setForm({ ...form, currentStateId: e.target.value === '' ? 0 : Number(e.target.value) })}
                  disabled={pipelineId <= 0}
                  fullWidth className="form-field"
                >
                  <MenuItem value="">{t('quotation.form.selectState')}</MenuItem>
                  {pipelineStates.map((s) => (
                    <MenuItem key={s.pipelineStateId} value={s.pipelineStateId}>
                      {pipelineStateLabel(s)}
                    </MenuItem>
                  ))}
                </TextField>
              </div>
            )}

            <TextField
              required
              label={t('quotation.form.title')}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              fullWidth
              className="form-field"
            />

            <div className="qform__row">
              {/* 0 significa "nessun cliente": il valore è null, e il guard JS
                  al submit resta la cintura di sicurezza del required. */}
              <Autocomplete
                {...clientAutocompleteProps}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                value={form.businessId > 0 ? (client?.id === form.businessId ? client : { id: form.businessId, label: '' }) : null}
                onChange={(_, option) => {
                  setClient(option);
                  setForm((prev) => ({ ...prev, businessId: option ? Number(option.id) : 0 }));
                }}
                disabled={clientLocked}
                loading={clientLoading}
                fullWidth className="form-field"
                renderInput={(params) => (
                  <TextField {...params} required label={t('quotation.form.client')}
                    placeholder={t('quotation.form.selectClient')} />
                )}
              />
              {/* Il codice esterno lo scrive il gestionale, non l'utente: si mostra, se c'è, e non si cambia. */}
              {form.externalCode && (
                <TextField
                  label={t('quotation.externalCode')}
                  value={form.externalCode}
                  disabled
                  fullWidth className="form-field"
                />
              )}
            </div>

            <div className="qform__row">
              <TextField
                type="date"
                label={t('quotation.form.dueDate')}
                value={form.dueDate ?? ''}
                onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth className="form-field"
              />
              <TextField
                type="number"
                label={t('quotation.form.amount')}
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
                fullWidth className="form-field"
              />
            </div>

            {/* Lo stato si sceglie solo in modifica: in creazione il backend lo
                ignora e il preventivo nasce in bozza, quindi mostrarlo darebbe
                l'idea di una scelta che non viene rispettata. */}
            {isEdit && (
              <div className="qform__row">
                <TextField
                  select
                  label={t('quotation.form.status')}
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value as QuotationStatus })}
                  fullWidth className="form-field"
                >
                  {QUOTATION_STATUSES.map((s) => (
                    <MenuItem key={s.value} value={s.value}>{t(s.label)}</MenuItem>
                  ))}
                </TextField>
              </div>
            )}

            <TextField
              multiline rows={3}
              label={t('common.notes')}
              value={form.notes ?? ''}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              fullWidth className="form-field"
            />
          </div>
        </section>

        {/* Sotto-form contribuiti dai plugin. In creazione l'entità non
            esiste ancora e `entityId` vale 0: la convenzione è dichiarata nel
            contratto. La partecipazione al salvataggio è materia dello step
            12 di TS-87.
            Ogni contributo sta in un blocco suo: il plugin continua a rendere
            un `form-section-heading` + campi come prima, e' il wrapper del core
            a dargli la stessa scatola del blocco base — cosi' non serve
            toccare i plugin per allinearli al restyle. */}
        <QuotationFormContext.Provider value={formContext}>
          {extraContributors.map((c) => (
            <section key={c.key} className="blk qform__ext">
              <c.component
                entityId={partialSave?.quotationId ?? (isEdit && id ? Number(id) : 0)}
              />
            </section>
          ))}
        </QuotationFormContext.Provider>

        {/* Salvataggio parziale: il preventivo c'è, i campi di un plugin no.
            Non è un toast perché non è una notifica di passaggio — è uno
            stato in cui la pagina resta, con l'azione per uscirne. */}
        {partialSave && (
          <div className="form-partial-save">
            <p className="text-error">{t('quotation.form.partialSave')}</p>
            <Button type="button" variant="outlined" onClick={retryExtensions}>
              {t('quotation.form.retryExtensions')}
            </Button>
          </div>
        )}

        {/* Azioni a destra, come in fondo agli altri form del restyle: prima
            l'uscita, poi l'azione conclusiva. */}
        <div className="qform__bar">
          <Button type="button" variant="outlined" onClick={() => navigate(exitTo)}>
            {sourceMessageId ? t('quotation.form.back') : t('common.cancel')}
          </Button>
          <Button type="submit" variant="contained">
            {isEdit ? t('common.save') : t('quotation.createQuotation')}
          </Button>
        </div>
      </form>
    </>
  );
}
